import {gql} from '@apollo/client';
import React from 'react';
import _ from 'underscore';

import {Permissions} from 'auth/PermissionConstants';
import {StringIDFolder} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as NavConstants from 'constants/NavConstants';
import {ProjectSummary} from 'constants/SourceConstants';
import normalizeFolder from 'folders/util/normalizeFolder';
import {SearchSection} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {LoadingState} from 'models/reduceLoadingState';
import {RootCrumb} from 'nav/Breadcrumbs';
import ExpandedProjectTable from 'nav/expanded/ExpandedProjectTable';
import ListingSettingsDropdown from 'nav/ListingSettingsDropdown';
import NewProjectButton from 'nav/NewProjectButton';
import ListingHeader from 'nav/sidepanel/header/ListingHeader';
import {getSortItemSections} from 'nav/sidepanel/header/SidepanelListingSettingsDropdown';
import CompactResultsPagerWrapper from 'nav/sidepanel/listing/CompactResultsPagerWrapper';
import {ProjectPreferences} from 'nav/sidepanel/listing/ProjectListingConstants';
import {getDefaultProjectSearchParams} from 'nav/sidepanel/listing/ProjectsSearchParams';
import * as SearchURLs from 'nav/sidepanel/listing/SearchURLs';
import ToggleDropdown from 'nav/ToggleDropdown';
import {Party} from 'parties/PartyConstants';
import BenchlingProfiler from 'performance/BenchlingProfiler';
import {composeSidepanelSearchResultsRenderedEvent} from 'performance/processSearchEvents';
import preferences from 'Preferences';
import {FilterOption} from 'search/filters/base/FilterComponents';
import FiltersContainer from 'search/filters/FiltersContainer';
import QueryInput from 'search/QueryInput';
import SavedSearchHeader from 'search/saved-searches/SavedSearchHeader';
import {
  Filter,
  FilterKey,
  ListableItemExtras,
  LISTING_ERROR_MESSAGE,
  SearchParams,
  SearchType,
} from 'search/SearchConstants';
import * as SearchPreferences from 'search/SearchPreferences';
import withSearching, {NavigationUpdate, SearchProps} from 'search/withSearching';
import EntityStore from 'stores/EntityStore';
import * as nyc from 'ui-library/NYC';
import ResultsPager from 'ui-library/ResultsPager';
import {cycleColumnHeaderSort} from 'ui-library/scrollable/ScrollableTableUtil';
import APIUtil from 'util/APIUtil';

import {AVAILABLE_PAGE_SIZES} from '../PageSizeUtil';
import {ProjectsListingQuery} from './__generated/ProjectsListingQuery';
import ProjectList from './ProjectList';
import {translateCompositeToSightglass} from './ProjectsListingFilters';
import {ProjectsSidepanelContext} from './ProjectsSidepanelContext';

export const PROJECTS_LISTING_QUERY = gql`
  query ProjectsListingQuery($projectIds: [StrictString!]!, $folderIds: [StrictString!]!) @skipAudit {
    projects(ids: $projectIds) {
      ...ProjectSummaryGraphQL
      ... on ProjectNode {
        id
        permissions {
          admin
          appendable
          archivable
          canManageCollaborators
          readable
          unarchivable
          writable
          deletable
        }
        collaboratorsJson
        description
      }
      ...InaccessibleInfoGraphQL
    }
    folders(ids: $folderIds) {
      ...FolderWithPermissionsGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProjectSummary').fragment}
  ${getFragmentWrapper('FolderWithPermissions').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export type ProjectsListingProject = ProjectSummary & {
  permissions: Permissions;
  collaborators: Array<Party>;
  description?: string | null;
};

export type ProjectsListingFolder = StringIDFolder & ListableItemExtras;

interface BaseProjectsListingProps {
  isExpanded: boolean;
  onListFolder: (rootFolder: StringIDFolder) => void;
  availableSortKeys: Array<string>;
}

export interface ProjectsListingProps extends BaseProjectsListingProps {
  items: Array<ProjectsListingProject | ProjectsListingFolder>;
  availableSortKeys: Array<string>;
  areProjectsLoading: boolean;
  didLoadingError: boolean;

  // If set, then we will show Section tabs in the listing
  sectionTabs?: JSX.Element;
}

interface State {
  filters: Array<Filter>;
}

export class ProjectsListing extends React.Component<ProjectsListingProps & SearchProps> {
  override state: State = {
    filters: getDefaultProjectSearchParams().filters,
  };

  _handleChangeFilters(filters: Array<Filter>): void {
    this.setState({filters});
    this._rememberProjectFilters(filters);
    this.props.onUpdateFilters(translateCompositeToSightglass(filters));
  }

  _handleSearch = (searchParams: SearchParams): void => {
    this.props.onSearch(searchParams);
    SearchPreferences.rememberAppliedSortsToUserPreference(
      NavConstants.SECTIONS.PROJECTS,
      searchParams.sorts
    );
  };

  _rememberProjectFilters = (filters: Array<Filter>): void => {
    const sharedWithFilter = filters.find((filter) => filter.key === FilterKey.SHARED_WITH);
    if (sharedWithFilter) {
      preferences.set(ProjectPreferences.LAST_SELECTED_PROJECT_FILTER, sharedWithFilter.value);
    } else {
      preferences.set(ProjectPreferences.LAST_SELECTED_PROJECT_FILTER, null);
    }

    const attributeFilter = filters.find((filter) => filter.key === FilterKey.PROJECT_ATTRIBUTE);
    if (attributeFilter) {
      preferences.set(
        ProjectPreferences.LAST_SELECTED_PROJECT_ATTRIBUTE_FILTER,
        attributeFilter.value ?? null
      );
    } else {
      preferences.set(ProjectPreferences.LAST_SELECTED_PROJECT_ATTRIBUTE_FILTER, null);
    }
  };

  _handleChangeHeaderSort = (sortKey: string): void => {
    const newSort = cycleColumnHeaderSort(this.props.searchParams.sorts, sortKey);
    this._updateSortParams(newSort.key, newSort.reverse);
  };

  _updateSortParams = (sortKey: string, reverse: boolean): void => {
    this.props.onUpdateSortOrder([{key: sortKey, reverse}]);
  };

  override render(): JSX.Element {
    const currentUser = EntityStore.currentUser();
    const teams = currentUser.teams ? currentUser.teams : [];
    const organizations = currentUser.organizations ? currentUser.organizations : [];
    const pagination = {
      offset: this.props.searchParams.offset,
      limit: this.props.searchParams.limit,
      total: this.props.searchResults?.total ?? 0,
    };
    const settingsDropdown = (
      <ListingSettingsDropdown
        sortConfig={{
          availableSortOptions: getSortItemSections(this.props.availableSortKeys),
          sorts: this.props.searchParams.sorts,
          onSetSort: this._updateSortParams,
        }}
        pageSizeConfig={{
          pageSize: this.props.searchParams.limit,
          availablePageSizes: AVAILABLE_PAGE_SIZES,
          onSetPageSize: this.props.onUpdatePageSize,
        }}
      />
    );
    const header = (
      <ProjectsSidepanelContext.Consumer>
        {({rootCrumbName}) => (
          <ListingHeader
            isExpanded={this.props.isExpanded}
            sectionTabs={this.props.sectionTabs}
            buttons={
              <div>
                <ToggleDropdown className="u-mini-spacing-right" expanded={this.props.isExpanded} />
                <NewProjectButton compact={!this.props.isExpanded} openResult={this.props.onListFolder} />
              </div>
            }
            crumbs={<RootCrumb text={rootCrumbName} />}
            filtersContainer={
              <FiltersContainer
                primaryFilterOptions={
                  _.compact([
                    {
                      filterKey: FilterKey.SHARED_WITH,
                      extraProps: {
                        organizations,
                        teams,
                        showMyProjects: true,
                        showSharedWithMe: true,
                      },
                    },
                    {
                      filterKey: FilterKey.PROJECT_ATTRIBUTE,
                    },
                  ]) as Array<FilterOption>
                }
                filters={this.state.filters}
                onChangeFilters={(filters) => {
                  this._handleChangeFilters(filters);
                }}
                selectedSchema={null}
              />
            }
            queryInput={
              <QueryInput
                query={this.props.searchParams.query}
                onUpdateQuery={this.props.onUpdateQueryString}
              />
            }
            showShadow={true}
            settingsDropdown={settingsDropdown}
            showSettingsDropdownInlineInExpandedView={false}
            savedSearchHeader={
              <SavedSearchHeader
                searchSection={SearchSection.PROJECT_ITEMS}
                onSelectSavedSearch={this.props.onSelectSavedSearch}
                activeSavedSearch={this.props.activeSavedSearch}
                key="saved-searches-dropdown"
                onSearch={this.props.onSearch}
                searchParams={this.props.searchParams}
                isExpanded={this.props.isExpanded}
              />
            }
          />
        )}
      </ProjectsSidepanelContext.Consumer>
    );

    const SIDEPANEL_SEARCH_RESULTS_RENDERED_EVENT = composeSidepanelSearchResultsRenderedEvent();

    return this.props.isExpanded ? (
      <nyc.Container
        className="sidepanel expandedContainer _test-projects-listing"
        size="lg"
        dataTestAttrs={{component: 'ProjectListing', element: 'root', key: 'expanded'}}
      >
        <nyc.Row>
          <nyc.Col lg={1} />
          <nyc.Col lg={20}>{header}</nyc.Col>
          <nyc.Col lg={1} />
        </nyc.Row>
        <nyc.Row aria-labelledby={NavConstants.Section.PROJECTS} fluid={true} role="tabpanel">
          <nyc.Col lg={1} />
          <nyc.Col lg={20} className="u-flex-display u-vertical-flex">
            {this.props.didLoadingError ? (
              <div className="sidepanel-fadedMessage">{LISTING_ERROR_MESSAGE}</div>
            ) : (
              <>
                <div className="u-flex-display">
                  <ResultsPager
                    isLoading={this.props.areProjectsLoading}
                    pagination={pagination}
                    setOffset={this.props.onUpdateOffset}
                  />
                  {settingsDropdown}
                </div>
                <div className="u-flex-grow u-flex-display u-justify-content-center">
                  <BenchlingProfiler {...SIDEPANEL_SEARCH_RESULTS_RENDERED_EVENT}>
                    <ExpandedProjectTable
                      finderId={NavConstants.PROJECTS_FINDER_ID}
                      openResult={this.props.onListFolder}
                      items={this.props.items}
                      areProjectsLoading={this.props.areProjectsLoading}
                      sorts={this.props.searchParams.sorts}
                      onChangeSort={this._handleChangeHeaderSort}
                    />
                  </BenchlingProfiler>
                </div>
              </>
            )}
          </nyc.Col>
          <nyc.Col lg={1} />
        </nyc.Row>
      </nyc.Container>
    ) : (
      <nyc.Container
        className="sidepanel _test-projects-listing"
        size="sidepanel"
        dataTestAttrs={{component: 'ProjectListing', element: 'root', key: 'collapsed'}}
      >
        <nyc.Row>{header}</nyc.Row>
        <nyc.Row
          aria-labelledby={NavConstants.Section.PROJECTS}
          fluid={true}
          className="u-position-relative"
          role="tabpanel"
        >
          <div className="u-absolute-scroll-container">
            {this.props.didLoadingError ? (
              <div className="sidepanel-fadedMessage">{LISTING_ERROR_MESSAGE}</div>
            ) : (
              <CompactResultsPagerWrapper pagination={pagination} setOffset={this.props.onUpdateOffset}>
                <BenchlingProfiler {...SIDEPANEL_SEARCH_RESULTS_RENDERED_EVENT}>
                  <ProjectList
                    finderId={NavConstants.PROJECTS_FINDER_ID}
                    openResult={this.props.onListFolder}
                    items={this.props.items}
                    areProjectsLoading={this.props.areProjectsLoading}
                  />
                </BenchlingProfiler>
              </CompactResultsPagerWrapper>
            )}
          </div>
        </nyc.Row>
      </nyc.Container>
    );
  }
}

type QueryingProjectsListingProps = BaseProjectsListingProps & SearchProps;

export function QueryingProjectsListing(props: QueryingProjectsListingProps): JSX.Element | null {
  const searchResultsIds = props.searchResults ? props.searchResults.ids : [];
  const projectsListingResults = useBenchlingQuery<ProjectsListingQuery>(PROJECTS_LISTING_QUERY, {
    variables: {
      projectIds: searchResultsIds.filter((id) => APIUtil.apiIdToType(id) === ModelTypeConstants.SOURCE),
      folderIds: searchResultsIds.filter((id) => APIUtil.apiIdToType(id) === ModelTypeConstants.FOLDER),
    },
  });

  if (projectsListingResults.type === QueryResultType.ERROR || props.loadingState === LoadingState.FAILED) {
    return <ProjectsListing {...props} items={[]} areProjectsLoading={false} didLoadingError={true} />;
  }
  if (
    projectsListingResults.type === QueryResultType.PENDING ||
    props.loadingState === LoadingState.LOADING
  ) {
    return <ProjectsListing {...props} items={[]} areProjectsLoading={true} didLoadingError={false} />;
  }

  const projects = projectsListingResults.data.projects.filter((project) => !isInaccessible(project));
  const folders = projectsListingResults.data.folders.filter((folder) => !isInaccessible(folder));

  const itemsGQL: Array<ProjectsListingProject | ProjectsListingFolder> = [
    ...projects.map((project) => ({
      ...graphQLToREST('ProjectSummary', project),
      permissions: project.permissions,
      collaborators: project.collaboratorsJson,
      description: project.description ? project.description : undefined,
    })),
    ...folders.map((folder) => ({
      ...normalizeFolder(graphQLToREST('FolderWithPermissions', folder)),
      permissions: {
        ...folder.permissions,
        batchesWritable: Boolean(folder.permissions.batchesWritable),
        bioentitiesAppendable: Boolean(folder.permissions.bioentitiesAppendable),
        canCreateRequest: Boolean(folder.permissions.canCreateRequest),
        movable: Boolean(folder.permissions.movable),
        subfolderCreatable: Boolean(folder.permissions.subfolderCreatable),
      },
    })),
  ];
  const itemsById = _.indexBy(itemsGQL, 'id');

  return (
    <ProjectsListing
      {...props}
      didLoadingError={false}
      areProjectsLoading={false}
      items={searchResultsIds.filter((id) => itemsById[id]).map((id) => itemsById[id])}
    />
  );
}

function getProjectsURL(
  prevProps: ProjectsListingProps & SearchProps,
  props: ProjectsListingProps & SearchProps
): NavigationUpdate | null {
  if (!props.isExpanded) {
    return null;
  }
  return {url: SearchURLs.getNavigationURL('/projects', props.searchParams, props.filterErrors)};
}

export default withSearching(QueryingProjectsListing, SearchType.PROJECTS, NavConstants.SECTIONS.PROJECTS, {
  defaultSearchParams: {
    ...getDefaultProjectSearchParams(),
    filters: translateCompositeToSightglass(getDefaultProjectSearchParams().filters),
  },
  getNavigationUpdate: getProjectsURL,
});
